import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import useIsMobile from '../hooks/useIsMobile';
import { getUserKey } from '../utils/userStorage';
import { FiBarChart2, FiCheckCircle, FiClock, FiCoffee, FiFileText, FiFolder, FiSmile, FiTarget } from 'react-icons/fi';

// SMM PLATFORMALARI UCHUN IKONKALAR
const PlatformIcon = ({ name, size = 16 }) => {
  switch (name) {
    case 'YouTube':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="#ef4444" style={{ display: 'block' }}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
    case 'Instagram':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>;
    case 'Facebook':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877f2" style={{ display: 'block' }}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    case 'Telegram':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="#0ea5e9" style={{ display: 'block' }}><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.503-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.481-.428-.008-1.252-.241-1.865-.44-.751-.244-1.348-.373-1.296-.788.027-.216.324-.437.893-.664 3.505-1.524 5.844-2.529 7.018-3.013 3.344-1.383 4.039-1.623 4.493-1.631.1.002.321.027.465.145.121.101.154.241.168.341.015.102.033.334.017.502z"/></svg>;
    default:
      return null;
  }
};

// --- ANIMATSIYA VARIANTLARI ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

const Dashboard = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useI18n();
  const { currentRole } = useAuth();
  const isMobile = useIsMobile();

  const getRoleWelcomeText = (role) => {
    const roleMap = {
      Admin: 'Xush kelibsiz, Administrator!',
      'SMM Menejer': 'Xush kelibsiz, SMM Menejeri!',
      Dizayner: 'Xush kelibsiz, Dizayner!',
      Montajor: 'Xush kelibsiz, Montajchi!',
      Kopirayter: 'Xush kelibsiz, Kopirayter!',
      'VFX Mutaxasis': 'Xush kelibsiz, VFX Mutaxasis!',
      'AI Mutaxasis': 'Xush kelibsiz, AI Mutaxasis!',
    };

    return roleMap[role] || 'Xush kelibsiz, Mutaxassis!';
  };

  // State'lar
  const [stats, setStats] = useState({
    activeProjects: 0,
    todayPosts: 0,
    publishedPosts: 0,
    pendingPosts: 0,
    platforms: { Instagram: 0, Telegram: 0, YouTube: 0, Facebook: 0 }
  });
  const [upcomingPosts, setUpcomingPosts] = useState([]);

  // Bugungi sanani olish
  const getTodayString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const todayStr = getTodayString();

  // LocalStorage dan ma'lumotlarni o'qib, statistikani hisoblash
  useEffect(() => {
    const savedProjects = localStorage.getItem(getUserKey('projects'));
    if (savedProjects) {
      const parsedProjects = JSON.parse(savedProjects);

      let activeProjCount = 0;
      let todayPostsCount = 0;
      let pubCount = 0;
      let pendCount = 0;
      let platStats = { Instagram: 0, Telegram: 0, YouTube: 0, Facebook: 0 };
      let upcoming = [];

      parsedProjects.forEach(proj => {
        if (!proj.isArchived) {
          activeProjCount++;
          
          proj.posts.forEach(post => {
            if (!post.isArchived) {
              // Yaqin kunlardagi postlarni yig'ish (bugun va kelajakdagi)
              if (post.date >= todayStr) {
                upcoming.push({ ...post, projectName: proj.name });
              }
              // Bugungi postlar
              if (post.date === todayStr) todayPostsCount++;

              // Platformalar va statuslar bo'yicha hisoblash
              post.platforms.forEach(plat => {
                if (platStats[plat.name] !== undefined) platStats[plat.name]++;
                if (plat.status === 'published') pubCount++;
                if (plat.status === 'pending') pendCount++;
              });
            }
          });
        }
      });

      // Yaqin kunlardagi postlarni sanasi bo'yicha tartiblash
      upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));

      setStats({
        activeProjects: activeProjCount,
        todayPosts: todayPostsCount,
        publishedPosts: pubCount,
        pendingPosts: pendCount,
        platforms: platStats
      });
      setUpcomingPosts(upcoming.slice(0, 5)); // Faqat eng yaqin 5 tasini olamiz
    }
  }, [todayStr]);

  // Grafik uchun eng ko'p platformani topish (foiz hisoblash uchun)
  const maxPlatformCount = Math.max(...Object.values(stats.platforms), 1);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ padding: isMobile ? '16px' : '30px', color: isLight ? '#0f172a' : '#fff' }}>
      
      {/* SARLAVHA QISMI */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 5px 0', color: 'var(--accent-color)' }}>
          {getRoleWelcomeText(currentRole || 'SMM Menejer')} <FiSmile size={22} aria-hidden="true" />
        </h1>
        <p style={{ margin: 0, opacity: 0.7, fontSize: '14px' }}>{t('dashboard.subtitle')}</p>
      </div>

      {/* TOP KARTALAR (STATISTIKA) */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: isMobile ? '12px' : '20px', marginBottom: isMobile ? '20px' : '30px' }}>
        
        <motion.div variants={itemVariants} whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(var(--accent-color-rgb), 0.2)' }} className="glass" style={{ padding: '20px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(var(--accent-color-rgb), 0.15)', color: 'var(--accent-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiFolder size={22} strokeWidth={1.8} /></div>
          <div>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>{t('dashboard.cards.activeProjects')}</p>
            <h2 style={{ margin: '5px 0 0 0', fontSize: '24px' }}>{stats.activeProjects} ta</h2>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(245,158,11,0.2)' }} className="glass" style={{ padding: '20px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiTarget size={22} strokeWidth={1.8} /></div>
          <div>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>{t('dashboard.cards.todayPosts')}</p>
            <h2 style={{ margin: '5px 0 0 0', fontSize: '24px' }}>{stats.todayPosts} ta</h2>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(34,197,94,0.2)' }} className="glass" style={{ padding: '20px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiCheckCircle size={22} strokeWidth={1.8} /></div>
          <div>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>{t('dashboard.cards.published')}</p>
            <h2 style={{ margin: '5px 0 0 0', fontSize: '24px' }}>{stats.publishedPosts} ta</h2>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(168,85,247,0.2)' }} className="glass" style={{ padding: '20px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiClock size={22} strokeWidth={1.8} /></div>
          <div>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>{t('dashboard.cards.pending')}</p>
            <h2 style={{ margin: '5px 0 0 0', fontSize: '24px' }}>{stats.pendingPosts} ta</h2>
          </div>
        </motion.div>

      </motion.div>

      {/* ASOSIY KONTENT: GRAFIK VA RO'YXAT */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr', gap: '20px', alignItems: 'start' }}>
        
        {/* CHAP QISM: Platformalar bo'yicha statistika (Custom Bar Chart) */}
        <motion.div variants={itemVariants} initial="hidden" animate="visible" className="glass" style={{ padding: '25px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiBarChart2 size={17} aria-hidden="true" /> {t('dashboard.platformActivity')}</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {Object.keys(stats.platforms).map((plat, index) => {
              const count = stats.platforms[plat];
              const percentage = Math.round((count / maxPlatformCount) * 100) || 0;
              
              // Platformalarga qarab progress bar rangi
              let barColor = '#3b82f6';
              if(plat === 'Instagram') barColor = 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';
              if(plat === 'YouTube') barColor = '#ef4444';
              if(plat === 'Facebook') barColor = '#1877f2';
              if(plat === 'Telegram') barColor = '#0ea5e9';

              return (
                <div key={index} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><PlatformIcon name={plat} size={14} /> {plat}</span>
                    <span>{count} ta</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', borderRadius: '5px', overflow: 'hidden' }}>
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: `${percentage}%` }} 
                      transition={{ duration: 1, ease: "easeOut", delay: index * 0.2 }}
                      style={{ height: '100%', background: barColor, borderRadius: '5px' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* O'NG QISM: Yaqinlashayotgan kontentlar */}
        <motion.div variants={itemVariants} initial="hidden" animate="visible" className="glass" style={{ padding: '25px', borderRadius: '16px', background: isLight ? '#fff' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiFileText size={17} aria-hidden="true" /> {t('dashboard.upcoming.title')}</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {upcomingPosts.length > 0 ? upcomingPosts.map((post, idx) => (
              <motion.div key={idx} whileHover={{ x: 5 }} style={{ padding: '12px 15px', borderRadius: '10px', background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)', borderLeft: `3px solid ${post.date === todayStr ? '#f59e0b' : '#3b82f6'}`, display: 'flex', flexDirection: 'column', gap: '5px', transition: 'all 0.2s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', background: 'rgba(37, 99, 235, 0.1)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{post.projectName}</span>
                    <span style={{ fontSize: '11px', opacity: 0.7, color: post.date === todayStr ? '#f59e0b' : 'inherit', fontWeight: post.date === todayStr ? 'bold' : 'normal' }}>
                    {post.date === todayStr ? t('dashboard.upcoming.todayLabel') : post.date}
                  </span>
                </div>
                <h4 style={{ margin: 0, fontSize: '14px' }}>{post.title}</h4>
                <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                  {post.platforms.map((p, i) => (
                    <span key={i} title={p.name}><PlatformIcon name={p.name} size={12} /></span>
                  ))}
                </div>
              </motion.div>
            )) : (
              <div style={{ textAlign: 'center', padding: '30px 0', opacity: 0.5 }}>
                <FiCoffee size={26} style={{ marginBottom: '10px' }} aria-hidden="true" />
                <p style={{ margin: 0, fontSize: '14px' }}>{t('dashboard.upcoming.empty')}</p>
              </div>
            )}
          </div>
        </motion.div>

      </div>

    </motion.div>
  );
};

export default Dashboard;